import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { OBJECT_STORES } from '../index-db';
import { IndexedDbService } from './indexed-db.service';
import { BehaviorSubject, combineLatest } from 'rxjs';

export enum INTERCOM_EVENTS {
    MYCARDS_OPENED = 'mycards-opened-cards-list',
}

@Injectable({
    providedIn: 'root',
})
export class IntercomService {
    private scriptId = 'intercom-script';
    private IntercomScriptLoadedSubject = new BehaviorSubject<boolean>(false);
    public isIntercomScriptLoaded$ =
        this.IntercomScriptLoadedSubject.asObservable();
    constructor(
        private readonly authService: AuthService,
        private readonly indexDBService: IndexedDbService
    ) {
        combineLatest([
            this.isIntercomScriptLoaded$,
            this.authService.isLoggedIn$,
        ]).subscribe(([isLoaded, loggedIn]) => {
            if (!isLoaded) {
                console.log('Intercom is not available yet.');
                return;
            }

            if (loggedIn) {
                const user = this.authService.getUser();
                this.initIntercomUser(user);
            } else {
                this.indexDBService
                    .getAll(OBJECT_STORES.USERS)
                    .subscribe((data) => {
                        if (data.length > 0) {
                            this.initIntercomUser(data[0]);
                        }
                    });
            }
        });
    }

    logIntercomEventOnLoad() {
        this.authService.isOpenedAsPWA$.subscribe((isPWA) => {
            if (isPWA) {
                this.trackEvent(INTERCOM_EVENTS.MYCARDS_OPENED);
            }
        });
    }

    initIntercomUser(user) {
        const intercomUser = {
            app_id: environment.intercomAppId,
            user_id: user.id,
        };
        this.bootIntercomUser(intercomUser);
        this.logIntercomEventOnLoad();
    }

    bootIntercomUser(intercomUser: {}) {
        (<any>window).Intercom('boot', intercomUser);
    }

    public trackEvent(event: string) {
        if (!event) {
            return;
        }
        (<any>window).Intercom('trackEvent', event);
    }

    public shutdownIntercom() {
        (<any>window).Intercom('shutdown');
    }

    /**
     * Dynamically adds the Intercom script to the document.
     */
    public loadIntercomScript(): void {
        const excludedRoutes = ['/mobile-landing', '/desktop-landing', '/'];
        const currentUrl = window.location.pathname;

        if (excludedRoutes.includes(currentUrl)) {
            console.log('Intercom script not loaded on excluded routes.');
            return;
        }

        if (document.getElementById('intercom-script')) {
            console.log('Intercom script already loaded.');
            return;
        }

        const script = document.createElement('script');
        script.id = 'intercom-script';
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${environment.intercomAppId}`;

        script.onload = () => {
            if (typeof (window as any).Intercom === 'function') {
                (<any>window).Intercom('reattach_activator');
                (<any>window).Intercom('update', {
                    app_id: environment.intercomAppId,
                });
                (<any>window).Intercom('update', {
                    hide_default_launcher: true,
                });
                this.IntercomScriptLoadedSubject.next(true);
            }
        };

        document.head.appendChild(script);
    }

    /**
     * Removes the Intercom script from the document and shuts down Intercom.
     */
    public removeIntercomScript(): void {
        const script = document.getElementById(this.scriptId);
        if (script) {
            script.remove();
            this.IntercomScriptLoadedSubject.next(false);
        }
    }
}
